/**
 * 统一 fetch 封装。
 *
 * 设计要点:
 * - 自动注入 `Authorization: Bearer <token>` (取自 auth store);
 * - 错误归一: 后端 4xx 形如 `{"detail": {"code": "...", "msg": "..."}}`,
 *   网络/解析错误归一为 ApiError, 调用方一律 try/catch ApiError;
 * - 204 No Content 返回 null;
 * - 不感知 router——成功/失败都由调用方决定后续动作(toast / navigate)。
 */

import { useAuthStore } from '@/stores/auth'

/** 后端错误码集合, 与 PROTOCOL.md 一致, 用 string 而非严格 enum 以便容错。*/
export type ApiErrorCode = string

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly payload: unknown

  constructor(code: ApiErrorCode, msg: string, status: number, payload?: unknown) {
    super(msg)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.payload = payload
  }
}

interface RequestOptions {
  /** 是否带 token; 默认 true。/auth/* 显式传 false。*/
  auth?: boolean
  /** 请求体, 任意可 JSON 序列化对象; 二进制场景以后再扩展。*/
  body?: unknown
  /** AbortSignal, 用于取消请求 (M2+ 可能用到)。*/
  signal?: AbortSignal
}

// Strip trailing slash so BASE + '/auth/...' never produces '//'.
const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { auth = true, body, signal } = options
  path = API_BASE + path

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  if (auth) {
    const authStore = useAuthStore()
    if (authStore.token) {
      headers['Authorization'] = `Bearer ${authStore.token}`
    }
  }

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    // fetch 抛异常只可能是网络错误或 abort
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError('aborted', 'request aborted', 0)
    }
    throw new ApiError('network', err instanceof Error ? err.message : 'network error', 0)
  }

  // 204: 无内容
  if (response.status === 204) {
    return null as T
  }

  // 解析 body
  let parsed: unknown = null
  const text = await response.text()
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      throw new ApiError('bad_response', `non-JSON response: ${text.slice(0, 200)}`, response.status)
    }
  }

  if (!response.ok) {
    // 后端约定: {"detail": {"code": "...", "msg": "..."}} 或 {"detail": "..."} (FastAPI 默认)
    const detail = (parsed as { detail?: unknown } | null)?.detail
    if (detail && typeof detail === 'object') {
      const { code, msg } = detail as { code?: string; msg?: string }
      throw new ApiError(code || 'unknown', msg || response.statusText, response.status, parsed)
    }
    if (typeof detail === 'string') {
      throw new ApiError('error', detail, response.status, parsed)
    }
    throw new ApiError('error', response.statusText, response.status, parsed)
  }

  return parsed as T
}

export const http = {
  get:    <T>(path: string, options?: RequestOptions) => request<T>('GET',    path, options),
  post:   <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('POST', path, { ...options, body }),
  patch:  <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
}
